import React, { Component, ReactNode, ErrorInfo } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '../contexts/AuthContext';
import { ContentProvider } from '../contexts/ContentContext';
import { AppRoutes } from '../router';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { ToastProvider } from '../contexts/ToastContext';
import './index.css';

// --- START: Error Boundary Component ---
interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: undefined
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught application error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const isPayPalError = this.state.error?.message.includes('paypal') || this.state.error?.stack?.includes('paypal');
      const errorMessage = isPayPalError
        ? "Could not load the payment module. This can happen if you are offline or have a network issue. Please check your connection and refresh the page."
        : "Something went wrong and the application could not recover. Please refresh the page to continue.";

      return (
        <div style={{ margin: 'auto', padding: '2rem', maxWidth: '600px', textAlign: 'center', color: '#7f1d1d', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', marginTop: '50px' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#b91c1c' }}>Application Error</h1>
          <p style={{ marginTop: '1rem' }}>{errorMessage}</p>
        </div>
      );
    }

    return this.props.children;
  }
}
// --- END: Error Boundary Component ---

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const paypalClientId = import.meta.env.VITE_PAYPAL_CLIENT_ID;

if (!paypalClientId) {
  console.error(
    "FATAL: PayPal Client ID is not configured. " +
    "Please set the VITE_PAYPAL_CLIENT_ID environment variable in your deployment settings."
  );
}

// FIX: Updated PayPal script options to use camelCase property names (`clientId`, `enableFunding`) instead of kebab-case (`client-id`, `enable-funding`) to match the `ReactPayPalScriptOptions` type definition.
const initialOptions = {
  clientId: paypalClientId || '', // Fallback to empty string to avoid crash, PayPal SDK will throw an error.
  intent: "subscription",
  vault: true,
  enableFunding: "card",
};

const app = (
  <React.StrictMode>
    <PayPalScriptProvider options={initialOptions}>
      <BrowserRouter>
        <ContentProvider>
          <AuthProvider>
            <ToastProvider>
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
            </ToastProvider>
          </AuthProvider>
        </ContentProvider>
      </BrowserRouter>
    </PayPalScriptProvider>
  </React.StrictMode>
);

// For React 19, createRoot handles both initial render and hydration automatically.
// The previous logic with hydrateRoot is deprecated and has been removed.
const root = ReactDOM.createRoot(rootElement);
root.render(app);